<script lang="ts" setup generic="T extends ProductSearchGolf | GolfLite">
  import type { LatLngTuple, Map } from 'leaflet'

  import type { Nullable } from '~/core/types'
  import type { ProductSearch, ProductSearchGolf } from '~/core/types/products'
  import type { GolfLite } from '~/core/types/shared'

  import 'leaflet/dist/leaflet.css'

  import BngResultsMapMarker from './ResultsMapMarker.vue'
  import ResultsResumeList from './ResultsResumeList.vue'

  type Props = {
    items: T[]
    focused?: Nullable<number>
    focusedProduct?: Nullable<ProductSearch>
    readOnly?: boolean
    displayPrice?: boolean
    isGreenFee?: boolean
  }
  const props = defineProps<Props>()

  const zoom = ref(6)
  const center = ref([46.5348177, 2.7523157])
  const url = ref('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png')
  const attribution = ref('&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors')

  const selectedGolfId = ref<Nullable<number>>(null)
  const highlightedGolfMarkers = ref<number[]>([])

  const mapRef = useTemplateRef('mapRef')

  function initMap() {
    updateMap(props.items)
  }

  function updateMap(g: T[]) {
    if (!g.length || !mapRef.value) {
      return
    }
    const markers = g
      .map(({ position }) => [position.latitude || 0, position.longitude || 0] as LatLngTuple)
      .filter(([lat, lng]) => lat !== 0 && lng !== 0)

    const map = mapRef.value.leafletObject as Map
    map?.fitBounds(markers, {
      animate: true,
      duration: 0.5,
      easeLinearity: 1,
    })
  }
  watch(() => props.items, (g) => {
    updateMap(g)
  }, { immediate: true })

  watch(() => props.focused, (newVal, oldVal) => {
    if (newVal === oldVal) {
      return
    }
    highlightedGolfMarkers.value = [newVal ?? 0]
  })

  watch(() => props.focusedProduct, (newVal, oldVal) => {
    if (newVal === oldVal) {
      return
    }
    highlightedGolfMarkers.value = newVal?.golfs.map((g) => g.id) ?? []
  })

  function handleMarkerClick(golf: T) {
    selectedGolfId.value = golf.id
    highlightedGolfMarkers.value = [golf.id]
  }

  function updateZoom(z: number) {
    zoom.value = z
  }

</script>

<template>
  <div class="c-results-map">
    <LMap
      ref="mapRef"
      :style="{height: '100%', width: '100%'}"
      :zoom="zoom"
      :center="center"
      :options="{ zoomSnap: 0.5 }"
      :use-global-leaflet="false"
      @update:zoom="updateZoom"
      @ready="initMap"
    >
      <LTileLayer
        :url="url"
        :attribution="attribution"
      />
      <div
        v-for="(item, index) in items"
        :key="`golf-marker-${index}`"
      >
        <BngResultsMapMarker
          :zoom="zoom"
          :readonly="readOnly"
          :golf="item"
          :display-price="displayPrice"
          :highlighted-golf-markers="highlightedGolfMarkers"
          @click="handleMarkerClick"
        />
      </div>
    </LMap>
    <ResultsResumeList
      v-if="isGreenFee"
      :golfs="items"
      :selected-golf-id="selectedGolfId"
    />
  </div>
</template>
<style scoped>
.c-results-map {
  position: relative;
  z-index: 1;
  display: flex;
  overflow: hidden;
  width: 100%;
  height: 100%;
  flex: 0 0 100%;
  flex-direction: column;

  &:deep(.leaflet-popup) {
    display: none;

    .leaflet-popup-content-wrapper {
      background: transparent;
      box-shadow: none;

      .leaflet-popup-content {
        margin: 0;

        p {
          margin: 0;
        }

        .c-paper {
          width: 260px;
          margin: 0;
          font-size: 1rem;
        }
      }
    }

    .leaflet-popup-close-button,
    .leaflet-popup-tip-container {
      display: none;
    }
  }

  .vue2leaflet-map {
    flex: 1 1 45%;
  }

  @media screen and (width >= 1024px) {
    &:deep(.leaflet-popup) {
      display: block;
    }

    .vue2leaflet-map {
      flex: 1 1 100%;
    }

    .c-results-resume-list {
      display: none;
    }
  }
}
</style>
